import { useState } from "react"
import { ZodError, ZodSchema, ZodObject } from "zod"

type FormErrors = {
  [key: string]: string | undefined
}

type ValidationResult<T> = {
  formData: T
  errors: FormErrors
  handleChange: (e: React.ChangeEvent<HTMLInputElement>, type?: string) => void
  validateField: (name: keyof T, value: any) => void
  validateForm: () => boolean
  isValidIsraeliID: (id: string) => boolean
  isValidEmail: (id: string) => boolean
}

type ValidationError = {
  errors: { message: string }[]
}

function useFormValidation<T extends { [key: string]: any }>(
  initialValues: T,
  validationSchema: ZodSchema<T>,
): ValidationResult<T> {
  const [formData, setFormData] = useState(initialValues)
  const [errors, setErrors] = useState<FormErrors>({})

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type?: string,
  ) => {
    const { name, value, checked } = e.target

    const newValue = type === "checkbox" ? checked : value
    setFormData((prevData) => ({ ...prevData, [name]: newValue }))

    // setFormData((prevData) => ({ ...prevData, [name]: value }))
    if (errors[name]) {
      setErrors((prevErrors: any) => ({ ...prevErrors, [name]: null }))
    }
  }

  const validateField = (name: keyof T, value: T[keyof T]) => {
    try {
      ;(validationSchema as unknown as ZodObject<any, any>)
        .pick({ name: true })
        .parse({ [name]: value })
      setErrors((prevErrors: any) => ({ ...prevErrors, [name]: null }))
    } catch (error) {
      if (error instanceof ZodError) {
        setErrors((prevErrors: any) => ({
          ...prevErrors,
          [name]:
            (error as ValidationError).errors[0]?.message || "Validation error",
        }))
      }
    }
  }

  const validateForm = () => {
    try {
      validationSchema.parse(formData)
      setErrors({})
      return true
    } catch (error) {
      if (error instanceof ZodError) {
        const newErrors: FormErrors = {}
        error.errors.forEach((err) => {
          if (typeof err.path[0] === "string") {
            newErrors[err.path[0]] = err.message
          }
        })
        setErrors(newErrors)
      }
      return false
    }
  }

  const isValidIsraeliID = (id: string): boolean => {
  // Remove any whitespace or dashes
  const cleanID: string = id.replace(/[\s-]/g, '');
  
  // Check if the ID contains only digits
  if (!/^\d+$/.test(cleanID)) {
    return false;
  }
  
  // Check length - must be 9 digits
  // If less than 9 digits, pad with leading zeros up to 9 digits
  let paddedID: string = cleanID;
  if (cleanID.length < 9) {
    paddedID = cleanID.padStart(9, '0');
  } else if (cleanID.length > 9) {
    return false;
  }
  
  // Apply the Luhn algorithm specific to Israeli IDs
  let sum: number = 0;
  
  for (let i: number = 0; i < 9; i++) {
    let digit: number = parseInt(paddedID.charAt(i));
    
    // For even positions (0-indexed), multiply by 1
    // For odd positions, multiply by 2 and sum digits if result > 9
    if (i % 2 === 0) {
      sum += digit;
    } else {
      digit *= 2;
      if (digit > 9) {
        digit = Math.floor(digit / 10) + (digit % 10);
      }
      sum += digit;
    }
  }
  
  // Check if the sum is divisible by 10
  return sum % 10 === 0;
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  
  return {
    formData,
    errors,
    handleChange,
    validateField,
    validateForm,
    isValidIsraeliID,
    isValidEmail
  }
}

export default useFormValidation
